import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';

import { mutate } from 'swr';

import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//utils
import { post } from '@/utils/client';
import { useSignUpStore } from '@/utils/zustand/providers/sign-up-provider';
//Hooks
import useRouterQuery from '@/hooks/utils/useRouterQuery';

// Types
import type { EmailType } from '@/types/brandedTypes/email';

//Components
import Button from '@/components/base/Button';
import Divider from '@/components/base/Divider/Divider';
import H1 from '@/components/base/Headings/H1';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import ButtonOAuthSignIn from '@/components/composite/Button/ButtonOAuthSignIn';

//Endpoint
import { LOGIN, SP_GROUPS, USER_SOCIAL_PROFILES } from '@/routes/apiEndpoints';

type SignInFormType = { email: EmailType; password: string };

const SignInForm = () => {
    const { t } = useTranslation();
    const { updateCurrentStep, currentStep } = useSignUpStore((state) => state);
    const { mutateUser } = useUser();
    const router = useRouter();
    const { clearUrlQueriesWithoutReload } = useRouterQuery();
    const { invalid_data, user_deleted, no_account } = router.query;

    // States
    const [errorMessage, setErrorMessage] = useState<string>(null);
    const [userDeletedMessage, setUserDeletedMessage] = useState<string>(null);
    const [passwordDisplayed, setPasswordDisplayed] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm<SignInFormType>();

    const onSubmit = async (data: SignInFormType) => {
        setErrorMessage(null);
        // CSRF cookie already set with first /user call
        try {
            await post(LOGIN, data);
            if (currentStep) {
                updateCurrentStep(null);
            }
            // Get user information if login is successful and mutate information with SWR
            await mutate(USER_SOCIAL_PROFILES, undefined);
            await mutate(SP_GROUPS, undefined);
            await mutateUser();
        } catch (e) {
            // Login fail
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors as Record<
                    keyof SignInFormType,
                    string[]
                >;
                if (errorsFields?.['email'] && errorsFields?.['email']?.[0] === 'failed') {
                    setErrorMessage(t('errors:error_invalid_email_or_password'));
                } else {
                    setErrorMessage(t('errors:given_data_invalid'));
                }
            } else {
                setErrorMessage(t('errors:error_occured_submit_data'));
            }
        }
    };

    useEffect(() => {
        if (invalid_data) {
            setErrorMessage(t('errors:given_data_invalid'));
            clearUrlQueriesWithoutReload();
        } else if (user_deleted) {
            setUserDeletedMessage(t('signin:remove_account.success'));
            clearUrlQueriesWithoutReload();
        } else if (no_account === '1') {
            setErrorMessage(t('signin:error_google_account_not_found'));
            clearUrlQueriesWithoutReload();
        }
    }, []);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {errorMessage ? (
                <InfoBanner
                    className="mb-5"
                    title={t('errors:error')}
                    message={errorMessage}
                    onClose={() => setErrorMessage(null)}
                />
            ) : null}
            {userDeletedMessage ? (
                <InfoBanner
                    className="mb-8"
                    type="success"
                    onClose={() => setUserDeletedMessage(null)}
                    title={t('commons:success')}
                    message={userDeletedMessage}
                />
            ) : null}
            <H1 className="mb-8 text-left">{t('signin:welcome_back_to_iconosquare')}</H1>
            <div className="flex flex-col gap-2">
                <ButtonOAuthSignIn id="signin-with-google" channel="google" type="sign_in" />
                <ButtonOAuthSignIn id="signin-with-apple" channel="apple" type="sign_in" />
            </div>

            <Divider className="my-5">
                <span className="text-sm text-icoGray-400">{t('commons:or')}</span>
            </Divider>
            <div className="space-y-3">
                <div>
                    <Label required htmlFor="email">
                        Email
                    </Label>
                    <Input
                        id="email"
                        placeholder="Email"
                        name="email"
                        type="email"
                        inputStyle="default_no_background"
                        error={errors?.email?.message}
                        {...register('email', { required: t('errors:error_email_required') })}
                    />
                </div>
                <div>
                    <Label required htmlFor="password">
                        {t('commons:password')}
                    </Label>
                    <Input
                        id="password"
                        type={passwordDisplayed ? 'text' : 'password'}
                        error={errors.password ? t('errors:error_password_required') : ''}
                        placeholder={t('commons:password')}
                        {...register('password', { required: true })}
                        inputStyle="default_no_background"
                        afterInput={
                            <button
                                type="button"
                                onClick={() => setPasswordDisplayed(!passwordDisplayed)}
                                className="flex items-center transition-colors hover:text-icoBlue">
                                <Icon
                                    id="button-display-password"
                                    icon={passwordDisplayed ? 'visible' : 'invisible'}
                                />
                            </button>
                        }
                    />
                </div>
            </div>
            <Button type="submit" isLoading={isSubmitting} className="mt-10 w-full">
                {t('signin:log_in')}
            </Button>
        </form>
    );
};

export default SignInForm;
